<template>
  <content-panel class="content-panel">
    <template v-slot:search>
      <search-box class="search-box">
        <el-form :inline="true" size="mini">
          <el-form-item label="文件名">
            <el-input
              v-model.trim="searchForm.fileName"
              placeholder="请输入文件名"
              @keyup.enter.native="handleSearch"
            />
          </el-form-item>
        </el-form>
        <template v-slot:right>
          <el-button type="primary" size="mini" @click="handleSearch">查询</el-button>
          <el-button size="mini" @click="handleReset">重置</el-button>
        </template>
      </search-box>
    </template>

    <table-panel>
      <template v-slot:header-left>
        <el-button type="primary" size="mini" @click="uploadDialogVisible = true">上传素材图</el-button>
      </template>
      <template v-slot:header-right>
        <el-button size="mini" @click="fetchList">刷新</el-button>
        <el-button size="mini" @click="handleBatchDownload">下载</el-button>
        <el-button type="danger" plain size="mini" @click="handleBatchDelete">删除</el-button>
      </template>

      <el-table
        :data="tableData.records"
        border
        size="mini"
        :height="maxHeight"
        v-loading="loading"
        @selection-change="handleSelectionChange"
        :header-cell-style="{ backgroundColor: '#F4F8FB', textAlign: 'center' }"
        :cell-style="{ textAlign: 'center' }"
      >
        <el-table-column type="selection" width="55" />
        <el-table-column label="预览" width="110">
          <template slot-scope="{ row }">
            <el-image
              class="preview-thumb"
              :src="row.filePath"
              :preview-src-list="[row.filePath]"
              fit="cover"
            />
          </template>
        </el-table-column>
        <el-table-column prop="fileName" label="文件名" min-width="220" show-overflow-tooltip />
        <el-table-column label="星级" width="140">
          <template slot-scope="{ row }">
            <i
              v-for="star in 5"
              :key="`${row.id}_${star}`"
              :class="star <= row.starLevel ? 'el-icon-star-on active-star' : 'el-icon-star-off empty-star'"
            />
          </template>
        </el-table-column>
        <el-table-column prop="launchTime" label="上传时间" min-width="170" />
        <el-table-column prop="uploader" label="上传人" min-width="120" />
        <el-table-column prop="size" label="大小" min-width="100" />
        <el-table-column label="操作" min-width="260" fixed="right">
          <template slot-scope="{ row }">
            <el-button type="text" @click="openScoreDialog(row)" v-permission="`ec-pc-new#levelSetting`">星级打分</el-button>
            <el-button
              type="text"
              v-clipboard:copy="row.url"
              v-clipboard:success="handleCopySuccess"
            >
              复制地址
            </el-button>
            <el-button type="text" @click="handleDownload(row)">下载</el-button>
            <el-button type="text" class="danger-text" @click="handleDelete(row)">删除</el-button>
          </template>
        </el-table-column>
      </el-table>

      <template v-slot:footer>
        <div class="pagination-wrap">
          <el-pagination
            @size-change="handleSizeChange"
            @current-change="handleCurrentChange"
            :current-page="tableData.pageNum"
            :page-size="tableData.pageSize"
            :page-sizes="[10, 20, 50, 100]"
            layout="total, sizes, prev, pager, next, jumper"
            :total="tableData.total"
          />
        </div>
      </template>
    </table-panel>

    <rating-dialog
      :visible.sync="scoreDialogVisible"
      :value="currentScoreRow.starLevel"
      @confirm="handleScoreConfirm"
    />

    <material-upload-dialog
      :visible.sync="uploadDialogVisible"
      title="上传素材图"
      :show-model="true"
      @confirm="handleUploadConfirm"
    />
  </content-panel>
</template>

<script>
import mockService from '../../mockService'
import RatingDialog from '../dialogs/RatingDialog.vue'
import MaterialUploadDialog from '../dialogs/MaterialUploadDialog.vue'

async function triggerDownload (url, fileName) {
  const response = await fetch(url)
  const blob = await response.blob()
  const objectUrl = window.URL.createObjectURL(blob)
  const link = document.createElement('a')
  link.href = objectUrl
  link.download = fileName
  document.body.appendChild(link)
  link.click()
  document.body.removeChild(link)
  window.URL.revokeObjectURL(objectUrl)
}

export default {
  name: 'AiMaterialContent',
  components: {
    RatingDialog,
    MaterialUploadDialog
  },
  props: {
    folderId: {
      type: String,
      default: ''
    },
    folderName: {
      type: String,
      default: ''
    }
  },
  data () {
    return {
      loading: false,
      uploadDialogVisible: false,
      scoreDialogVisible: false,
      searchForm: {
        fileName: ''
      },
      selectedRows: [],
      currentScoreRow: {},
      tableData: {
        records: [],
        total: 0,
        pageNum: 1,
        pageSize: 10
      }
    }
  },
  computed: {
    maxHeight () {
      return document.documentElement.clientHeight - 290
    }
  },
  watch: {
    folderId: {
      immediate: true,
      handler (val) {
        if (val) {
          this.tableData.pageNum = 1
          this.fetchList()
        }
      }
    }
  },
  methods: {
    buildDownloadName (name) {
      if (!name) return `AI素材下载_${Date.now()}.zip`
      return /\.[A-Za-z0-9]+$/.test(name) ? name : `${name}.zip`
    },
    pollDownloadTask (taskId, fallbackName) {
      const loadingInstance = this.$loading({
        lock: true,
        text: 'Loading',
        spinner: 'el-icon-loading',
        background: 'rgba(0, 0, 0, 0.7)'
      })
      return new Promise((resolve, reject) => {
        const timer = setInterval(() => {
          mockService.getStoreDecorationDownloadTaskInfo(taskId).then(async (taskRes) => {
            const taskData = taskRes.data || {}
            if (taskData.taskStatus === '执行完成') {
              clearInterval(timer)
              loadingInstance.close()
              if (!taskData.filePath) {
                reject(new Error('下载文件地址为空'))
                return
              }
              await triggerDownload(taskData.filePath, this.buildDownloadName(taskData.taskDesc || fallbackName))
              resolve(taskData)
            }
          }).catch((error) => {
            clearInterval(timer)
            loadingInstance.close()
            reject(error)
          })
        }, 5000)
      })
    },
    fetchList () {
      if (!this.folderId) return
      this.loading = true
      mockService.getAiMaterialList({
        folderId: this.folderId,
        fileName: this.searchForm.fileName,
        pageNum: this.tableData.pageNum,
        pageSize: this.tableData.pageSize
      }).then(res => {
        this.tableData = res.data
      }).finally(() => {
        this.loading = false
      })
    },
    handleSearch () {
      this.tableData.pageNum = 1
      this.fetchList()
    },
    handleReset () {
      this.searchForm.fileName = ''
      this.tableData.pageNum = 1
      this.fetchList()
    },
    handleSelectionChange (rows) {
      this.selectedRows = rows
    },
    handleSizeChange (pageSize) {
      this.tableData.pageNum = 1
      this.tableData.pageSize = pageSize
      this.fetchList()
    },
    handleCurrentChange (pageNum) {
      this.tableData.pageNum = pageNum
      this.fetchList()
    },
    openScoreDialog (row) {
      this.currentScoreRow = row
      this.scoreDialogVisible = true
    },
    handleScoreConfirm (starLevel) {
      mockService.scoreAiMaterial({
        id: this.currentScoreRow.id,
        starLevel
      }).then(() => {
        this.$message.success('打分成功')
        this.scoreDialogVisible = false
        this.fetchList()
      })
    },
    handleCopySuccess () {
      this.$message.success('复制成功')
    },
    handleUploadConfirm (payload) {
      mockService.uploadAiMaterial({
        ...payload,
        folderId: this.folderId
      }).then(res => {
        this.$message.success(res.message)
        this.uploadDialogVisible = false
        this.$emit('refresh-tree')
        this.fetchList()
      })
    },
    handleDelete (row) {
      this.$confirm(`确认删除素材“${row.fileName}”吗？`, '提示', { type: 'warning' }).then(() => {
        return mockService.deleteAiMaterials({ ids: [row.id] })
      }).then(res => {
        this.$message.success(res.message)
        this.fetchList()
      }).catch(() => {})
    },
    handleBatchDelete () {
      if (!this.selectedRows.length) {
        this.$message.warning('请先勾选需要删除的数据')
        return
      }
      const ids = this.selectedRows.map(item => item.id)
      this.$confirm('确认批量删除当前勾选素材吗？', '提示', { type: 'warning' }).then(() => {
        return mockService.deleteAiMaterials({ ids })
      }).then(res => {
        this.$message.success(res.message)
        this.fetchList()
      }).catch(() => {})
    },
    handleDownload (row) {
      mockService.downloadAiMaterials({ ids: [row.id] }).then(async (res) => {
        if (res && res.message) {
          await this.pollDownloadTask(res.message, row.fileName)
          this.fetchList()
          return
        }
        await triggerDownload(row.filePath || row.url, row.fileName)
        this.fetchList()
      })
    },
    handleBatchDownload () {
      if (!this.selectedRows.length) {
        this.$message.warning('请先勾选需要下载的数据')
        return
      }
      const ids = this.selectedRows.map(item => item.id)
      mockService.downloadAiMaterials({ ids }).then(async (res) => {
        if (res && res.message) {
          await this.pollDownloadTask(res.message, 'AI素材批量下载')
          this.fetchList()
          return
        }
        for (const item of this.selectedRows) {
          await triggerDownload(item.filePath || item.url, item.fileName)
        }
        this.fetchList()
      })
    }
  }
}
</script>

<style lang="stylus" scoped>
.content-panel
  height 100%
  background #fff
  border-radius 5px
  margin 0 0 0 4px

.preview-thumb
  width 72px
  height 72px
  object-fit cover
  border-radius 4px
  cursor pointer
  border 1px solid #ebeef5

.active-star
  color #F7BA2A
  font-size 16px
  margin 0 1px

.empty-star
  color #C0C4CC
  font-size 16px
  margin 0 1px

.danger-text
  color #F56C6C

.pagination-wrap
  display flex
  justify-content flex-end
  padding-top 10px
</style>
